import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { supabase } from "../../../utils/supabase";
import { INSTRUMENT_TYPE_OPTIONS } from "../../../utils/instrument-types";

const variableLabel = (value) =>
  INSTRUMENT_TYPE_OPTIONS.find((option) => option.value === value)?.label ||
  value;

export default function InstrumentCategoriesModal({
  open,
  onClose,
  onChanged,
  onCreated,
  defaultVariableType = "",
  closeAfterCreate = false,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");

  const [newName, setNewName] = useState("");
  const [newVariableTypes, setNewVariableTypes] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editVariableTypes, setEditVariableTypes] = useState([]);

  const filtered = useMemo(() => {
    const s = (search || "").trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => {
      const variableNames = (r.variable_types || [])
        .map(variableLabel)
        .join(" ")
        .toLowerCase();
      return (
        (r.name || "").toLowerCase().includes(s) || variableNames.includes(s)
      );
    });
  }, [rows, search]);

  function toggleVariable(setter, value) {
    setter((current) =>
      current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value]
    );
  }

  function notifyChanged() {
    onChanged?.();
    window.dispatchEvent(new CustomEvent("instrument-categories-changed"));
  }

  async function load() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("instrument_categories")
        .select("id,name,variable_types,created_at")
        .order("name", { ascending: true });

      if (error) throw error;
      setRows(data || []);
    } catch (e) {
      console.error("[InstrumentCategoriesModal] load error:", e);
      alert("No se pudieron cargar las categorías.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !defaultVariableType) return;
    setNewName("");
    setNewVariableTypes([defaultVariableType]);
  }, [open, defaultVariableType]);

  // Cerrar con ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  function resetEdit() {
    setEditId(null);
    setEditName("");
    setEditVariableTypes([]);
  }

  async function addCategory() {
    const name = (newName || "").trim();
    if (!name) return;
    if (newVariableTypes.length === 0) {
      alert("Seleccioná al menos una variable para la nueva categoría.");
      return;
    }

    setSaving(true);
    try {
      const { data: created, error } = await supabase
        .from("instrument_categories")
        .insert({ name, variable_types: newVariableTypes })
        .select("id,name,variable_types,created_at")
        .single();
      if (error) {
        if (String(error.code) === "23505") {
          alert("Ya existe una categoría con ese nombre.");
          return;
        }
        throw error;
      }
      setNewName("");
      setNewVariableTypes([]);
      await load();
      notifyChanged();
      onCreated?.(created);
      if (closeAfterCreate) onClose?.();
    } catch (e) {
      console.error("[InstrumentCategoriesModal] add error:", e);
      alert("No se pudo crear la categoría.");
    } finally {
      setSaving(false);
    }
  }

  async function startEdit(row) {
    setEditId(row.id);
    setEditName(row.name || "");
    setEditVariableTypes(row.variable_types || []);
  }

  async function saveEdit() {
    const name = (editName || "").trim();
    if (!editId) return;
    if (!name) {
      alert("El nombre no puede quedar vacío.");
      return;
    }
    if (editVariableTypes.length === 0) {
      alert("Seleccioná al menos una variable.");
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from("instrument_categories")
        .update({ name, variable_types: editVariableTypes })
        .eq("id", editId);

      if (error) {
        if (String(error.code) === "23505") {
          alert("Ya existe una categoría con ese nombre.");
          return;
        }
        throw error;
      }

      resetEdit();
      await load();
      notifyChanged();
    } catch (e) {
      console.error("[InstrumentCategoriesModal] update error:", e);
      alert("No se pudo guardar el cambio.");
    } finally {
      setSaving(false);
    }
  }

  async function removeCategory(row) {
    const ok = window.confirm(
      `¿Eliminar la categoría "${row.name}"?\n\nOjo: si hay instrumentos usando esta categoría, podría fallar por constraints o referencias.`
    );
    if (!ok) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from("instrument_categories")
        .delete()
        .eq("id", row.id);

      if (error) throw error;

      if (editId === row.id) resetEdit();

      await load();
      notifyChanged();
    } catch (e) {
      console.error("[InstrumentCategoriesModal] delete error:", e);
      alert("No se pudo eliminar la categoría. (Puede estar en uso)");
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  // Evita problemas en SSR / build raro
  if (typeof document === "undefined") return null;

  const modal = (
    <div className="fixed inset-0 z-[9999] flex items-start justify-center p-4 pt-24">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      {/* Modal */}
      <div
        className="relative w-full max-w-5xl rounded-xl border border-border/40 bg-surface/95 backdrop-blur shadow-industrial-md overflow-hidden"
        style={{ maxHeight: "calc(100vh - 6rem)" }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border/30">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              Categorías de instrumentos
            </h2>
            <p className="text-xs text-muted mt-0.5">
              Agregar / editar / eliminar categorías (tabla: instrument_categories)
            </p>
          </div>

          <button
            className="h-9 px-3 rounded-md border border-border/40 text-foreground/80 hover:bg-surface-2/60 transition"
            onClick={onClose}
            type="button"
          >
            Cerrar
          </button>
        </div>

        {/* ✅ El contenido scrollea dentro del modal */}
        <div className="p-5 space-y-4 overflow-auto" style={{ maxHeight: "calc(100vh - 12rem)" }}>
          {/* Crear */}
          <div className="rounded-xl border border-border/40 p-4 space-y-3">
            <div>
              <label className="block text-xs text-muted mb-1">Nueva categoría</label>
              <input
                className="field-lg w-full"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Ej: Termohigrómetro, Analizador de gases…"
                onKeyDown={(e) => {
                  if (e.key === "Enter") addCategory();
                }}
              />
            </div>

            <div>
              <div className="block text-xs text-muted mb-2">
                Variables que puede medir
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {INSTRUMENT_TYPE_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 rounded-md border border-border/30 px-3 py-2 text-sm cursor-pointer hover:bg-surface-2/50"
                  >
                    <input
                      type="checkbox"
                      checked={newVariableTypes.includes(option.value)}
                      onChange={() =>
                        toggleVariable(setNewVariableTypes, option.value)
                      }
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end">
              <button
                className="h-[42px] px-4 rounded-lg border border-primary/40 text-primary hover:bg-primary/10 transition disabled:opacity-60"
                onClick={addCategory}
                disabled={
                  saving ||
                  loading ||
                  !(newName || "").trim() ||
                  newVariableTypes.length === 0
                }
                type="button"
                title="Agregar"
              >
                {saving ? "Guardando…" : "Agregar categoría"}
              </button>
            </div>
          </div>

          {/* Buscar */}
          <div className="flex flex-col md:flex-row gap-2 md:items-end">
            <div className="flex-1">
              <label className="block text-xs text-muted mb-1">Buscar</label>
              <input
                className="field-lg w-full"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar categoría…"
              />
            </div>
            <div className="text-xs text-muted md:pb-2">
              {filtered.length} / {rows.length}
            </div>
          </div>

          {/* Lista */}
          <div className="rounded-xl border border-border/40 overflow-hidden">
            <div className="max-h-[420px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-surface/60 text-muted sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-3">Nombre</th>
                    <th className="text-left px-4 py-3">Variables</th>
                    <th className="text-right px-4 py-3 w-[240px]">Acciones</th>
                  </tr>
                </thead>

                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={3} className="px-4 py-6 text-center text-muted">
                        Cargando…
                      </td>
                    </tr>
                  )}

                  {!loading && filtered.length === 0 && (
                    <tr>
                      <td colSpan={3} className="px-4 py-6 text-center text-muted">
                        No hay categorías que coincidan.
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    filtered.map((r) => {
                      const isEditing = editId === r.id;

                      return (
                        <tr
                          key={r.id}
                          className="border-t border-border/20 hover:bg-surface-2/40"
                        >
                          <td className="px-4 py-3">
                            {!isEditing ? (
                              <span className="text-foreground">{r.name}</span>
                            ) : (
                              <input
                                className="field-lg w-full"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") saveEdit();
                                  if (e.key === "Escape") resetEdit();
                                }}
                              />
                            )}
                          </td>

                          <td className="px-4 py-3">
                            {!isEditing ? (
                              <div className="flex flex-wrap gap-1">
                                {(r.variable_types || []).length > 0 ? (
                                  r.variable_types.map((value) => (
                                    <span
                                      key={value}
                                      className="rounded-full border border-sky-400/30 bg-sky-400/10 px-2 py-0.5 text-xs text-sky-100"
                                    >
                                      {variableLabel(value)}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-xs text-amber-300">
                                    Sin clasificar
                                  </span>
                                )}
                              </div>
                            ) : (
                              <div className="grid grid-cols-1 gap-1">
                                {INSTRUMENT_TYPE_OPTIONS.map((option) => (
                                  <label
                                    key={option.value}
                                    className="flex items-center gap-2 text-xs"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={editVariableTypes.includes(
                                        option.value
                                      )}
                                      onChange={() =>
                                        toggleVariable(
                                          setEditVariableTypes,
                                          option.value
                                        )
                                      }
                                    />
                                    <span>{option.label}</span>
                                  </label>
                                ))}
                              </div>
                            )}
                          </td>

                          <td className="px-4 py-2">
                            <div className="flex justify-end gap-2">
                              {!isEditing ? (
                                <>
                                  <button
                                    className="h-9 px-3 rounded-md border border-amber-500/40 text-amber-200 hover:bg-amber-500/10 transition"
                                    onClick={() => startEdit(r)}
                                    type="button"
                                  >
                                    Editar
                                  </button>
                                  <button
                                    className="h-9 px-3 rounded-md border border-rose-500/40 text-rose-200 hover:bg-rose-500/10 transition"
                                    onClick={() => removeCategory(r)}
                                    type="button"
                                    disabled={saving}
                                  >
                                    Eliminar
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    className="h-9 px-3 rounded-md border border-primary/40 text-primary hover:bg-primary/10 transition disabled:opacity-60"
                                    onClick={saveEdit}
                                    type="button"
                                    disabled={
                                      saving ||
                                      !(editName || "").trim() ||
                                      editVariableTypes.length === 0
                                    }
                                  >
                                    Guardar
                                  </button>
                                  <button
                                    className="h-9 px-3 rounded-md border border-border/40 text-foreground/80 hover:bg-surface-2/60 transition"
                                    onClick={resetEdit}
                                    type="button"
                                    disabled={saving}
                                  >
                                    Cancelar
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="text-xs text-muted">
            Tip: Enter para guardar (en edición). Escape para cancelar.
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
